import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Tag, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default placeholder categories if none in database
  const defaultCategories = [
    { id: 'signage', name: 'Signage', description: 'Custom LED and neon signs for businesses', image: 'https://images.unsplash.com/photo-1636852305462-d22956abbceb?w=600' },
    { id: 'acrylic-displays', name: 'Acrylic Displays', description: 'Premium acrylic display units', image: 'https://images.unsplash.com/photo-1710888451601-9885aeb8afe2?w=600' },
    { id: 'menu-stands', name: 'Menu Stands', description: 'Elegant menu stands for hospitality', image: 'https://images.unsplash.com/photo-1642919854762-60313bb96a12?w=600' },
    { id: 'promotional-items', name: 'Promotional Items', description: 'Marketing and promotional products', image: 'https://images.unsplash.com/photo-1630689491691-b1b5b30b3840?w=600' },
    { id: 'large-format', name: 'Large Format', description: 'Banners and large format printing', image: 'https://images.unsplash.com/photo-1631025276172-ed04db772377?w=600' },
    { id: 'fabricated-letters', name: 'Fabricated Letters', description: '3D letters and dimensional signage', image: 'https://images.unsplash.com/photo-1556607356-d6a622ea735f?w=600' },
    { id: 'vehicle-branding', name: 'Vehicle Branding', description: 'Professional vehicle wraps and decals', image: 'https://images.unsplash.com/photo-1627445021452-c87e2f25e91d?w=600' },
    { id: 'lightboxes', name: 'Lightboxes', description: 'Illuminated display solutions', image: 'https://images.unsplash.com/photo-1630859169510-0667f4e0406e?w=600' },
  ];

  const displayCategories = categories.length > 0 ? categories : defaultCategories;

  if (loading) {
    return <div className="pt-32 px-6 text-center">Loading...</div>;
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <h1 className="text-5xl md:text-6xl font-black mb-6" data-testid="shop-title">ONLINE SHOP</h1>
        <p className="text-xl text-white/70 mb-4">Browse our product categories</p>
        
        {!user && (
          <div className="mb-12 p-8 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 shadow-[0_0_40px_rgba(212,175,55,0.4)]" data-testid="discount-notice">
            <div className="flex items-center gap-6">
              <Tag className="text-[#D4AF37]" size={48} />
              <div>
                <p className="text-2xl font-bold text-[#D4AF37] mb-2">Diamond Member Exclusive!</p>
                <p className="text-lg text-white/90 mb-4">Register now and get 10% off all products. Exclusive pricing for our valued customers.</p>
                <Button 
                  onClick={() => navigate('/auth')} 
                  className="border-2 border-[#D4AF37] bg-black hover:bg-black hover:shadow-[0_0_40px_rgba(212,175,55,0.8),0_0_60px_rgba(212,175,55,0.4)] text-[#D4AF37] hover:text-white transition-all duration-300"
                  data-testid="register-discount-button"
                >
                  Register for Diamond Access
                </Button>
              </div>
            </div>
          </div>
        )}

        {user && user.role === 'customer' && (
          <div className="mb-12 p-6 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-testid="member-discount-notice">
            <p className="text-[#D4AF37] font-bold text-lg">✓ Diamond Member Pricing Active - You're saving 10% on all products!</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              onClick={() => navigate(`/shop/category/${category.id}`)}
              className="group overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 cursor-pointer bg-white/5"
              data-testid={`shop-category-${index}`}
            >
              {/* Title Above Image */}
              <div className="p-4 bg-[#0a0a0a]">
                <h3 className="text-lg font-bold text-white/70 group-hover:text-[#D4AF37] transition-colors">{category.name}</h3>
              </div>
              <div className="w-full h-96 bg-black/20 overflow-hidden flex items-center justify-center">
                {category.image && (
                  <img 
                    src={category.image} 
                    alt=""
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {!category.image && <Layers size={48} className="text-white/20" />}
              </div>
              <div className="p-4 bg-[#0a0a0a]">
                <p className="text-white/60 text-sm whitespace-pre-line">{category.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Coming Soon Notice */}
        <div className="mt-12 p-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg text-center">
          <p className="text-[#D4AF37] font-bold mb-1">More Products Coming Soon</p>
          <p className="text-white/60 text-sm">We're continuously adding new categories and products. Check back regularly for updates!</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Shop;
